import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { Link, Navigate } from "react-router-dom";
import { ArrowLeft, Check, ChevronDown, Dice5, ExternalLink, Layers, Loader2, PlusCircle, RefreshCw, Sparkles } from "lucide-react";
import { Layout } from "../components/Layout";
import { LoadingSpinner, PageLoader } from "../components/LoadingSpinner";
import { Modal } from "../components/Modal";
import { useAuth } from "../contexts/AuthContext";
import { VISIBLE_AGENTIC_CODER_MODELS, type VisibleAgenticCoderModelId } from "../lib/agentModels";
import { dbDelete, dbInsert, dbSelect, dbUpdate, invokeFunction } from "../lib/api";
import { EMOJI_POOL } from "../data/emojiPool";

const ADMIN_EMAIL = "a.burkov@gmail.com";

const FALLBACK_LOGO_EMOJI = '✨';
const LOGO_EMOJI_REGEX = /(?:\p{Extended_Pictographic}|\p{Regional_Indicator})/u;

function normalizeLogoEmoji(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return '';

  try {
    const SegmenterCtor = (Intl as unknown as {
      Segmenter?: new (_locale?: string, options?: { granularity: 'grapheme' }) => {
        segment: (input: string) => Iterable<{ segment: string }>;
      };
    }).Segmenter;
    if (SegmenterCtor) {
      const segmenter = new SegmenterCtor(undefined, { granularity: 'grapheme' });
      for (const part of segmenter.segment(trimmed)) {
        return part.segment;
      }
    }
  } catch {
    // Fall back to simple code-point extraction.
  }

  return Array.from(trimmed)[0] ?? '';
}

function isLogoEmojiSet(value: string): boolean {
  return LOGO_EMOJI_REGEX.test(value.trim());
}

function mergeUniqueStrings(base: string[], additions: string[]): string[] {
  const merged = new Set(base);
  additions.forEach((item) => {
    if (item) merged.add(item);
  });
  return Array.from(merged);
}

const CODER_MODELS = VISIBLE_AGENTIC_CODER_MODELS;

type CoderModelId = VisibleAgenticCoderModelId;

interface FamilyProjectSummary {
  id: string;
  name: string;
  is_published: boolean;
  updated_at: string;
  family_creation_prompt: string | null;
  logo_emoji: string | null;
}

interface FamilyParameterDef {
  name: string;
  label: string;
  values: Array<{ value: string; label: string }>;
}

type FamilyCombination = Record<string, string>;

interface ExistingFamilyFork {
  id: string;
  name: string;
  description: string | null;
  slug: string | null;
  public_id: string | null;
  is_published: boolean;
  family_params: Record<string, string> | null;
  family_config_ref: string | null;
  updated_at: string;
  code_updated_at: string | null;
  code_updated_after_recreate: boolean;
}

interface FamilyConfigResponse {
  familyProject: {
    id: string;
    name: string;
  };
  config: {
    familyJsonPath: string;
    parameters: FamilyParameterDef[];
    combinations: FamilyCombination[];
    nameTemplate: string;
    slugTemplate: string;
    descriptionTemplate: string;
    titleTemplate: string;
    configRef: string;
  };
  existingForks: ExistingFamilyFork[];
}

interface GenerateForksResponse {
  summary: {
    selectedCombinations: number;
    createdForks: number;
    updatedForks: number;
    skippedCustomizedForks: number;
    unpublishedForks: number;
    syncFailures: number;
  };
  syncFailures: Array<{ projectId: string; error: string }>;
}

interface AgenticAuthResponse {
  token: string;
  serviceUrl: string;
  projectId: string;
  conversationId: string;
  isFirstMessage: boolean;
  isForkedProject: boolean;
  codeZipBase64: string | null;
  codeZipGcsPath: string | null;
}

const FAMILY_PROMPT_EXAMPLE = `Build a hidden family project for Pandoc file converters.

Use one shared Cloud Run backend and one shared generic frontend for all forks.
Forks must differ only by parameter values declared in family.json.

What to implement:
1. Backend that runs Pandoc and converts uploaded files.
2. Backend must read INPUT_FORMAT and OUTPUT_FORMAT from environment variables.
3. Backend must reject requests where user input does not match the fork's fixed parameters.
4. Do not hardcode one format pair in code.
5. Create family.json in project root with this shape:
{
  "parameters": [
    {
      "name": "input_format",
      "label": "Input Format",
      "values": [
        { "value": "markdown", "label": "Markdown" },
        { "value": "docx", "label": "DOCX" },
        { "value": "html", "label": "HTML" },
        { "value": "latex", "label": "LaTeX" }
      ]
    },
    {
      "name": "output_format",
      "label": "Output Format",
      "values": [
        { "value": "pdf", "label": "PDF" },
        { "value": "docx", "label": "DOCX" },
        { "value": "html", "label": "HTML" }
      ]
    }
  ],
  "allowed_combinations": [
    { "input_format": "markdown", "output_format": "pdf" },
    { "input_format": "docx", "output_format": "pdf" },
    { "input_format": "html", "output_format": "docx" },
    { "output_format": "html" }
  ],
  "name_template": "{input_format} to {output_format} Converter",
  "slug_template": "{input_format}-to-{output_format}-converter",
  "description_template": "Convert {input_format} files to {output_format} format.",
  "title_template": "{input_format} to {output_format} Converter"
}
6. Use web search to verify current Pandoc format names before finalizing family.json.
7. Ensure backend/Dockerfile/requirements.txt/cloud-run.json and frontend/index.html are complete and ready for deployment.`;

function combinationKey(combo: FamilyCombination): string {
  return Object.keys(combo).sort().map((k) => `${k}=${combo[k]}`).join("::");
}

function comboLabel(combo: FamilyCombination, parameters: FamilyParameterDef[]): string {
  return parameters.map((p) => {
    const val = combo[p.name];
    const opt = p.values.find((v) => v.value === val);
    return opt?.label || val || "?";
  }).join(" / ");
}

function interpolateTemplate(template: string, combo: FamilyCombination, paramDefs: FamilyParameterDef[]): string {
  let result = template;
  for (const [name, value] of Object.entries(combo)) {
    const def = paramDefs.find((p) => p.name === name);
    const label = def?.values.find((v) => v.value === value)?.label || value;
    result = result.replace(new RegExp(`\\{${name}\\}`, "g"), label);
  }
  return result;
}

function formatDateTime(value: string | null): string {
  if (!value) return "n/a";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "n/a";
  return parsed.toLocaleString();
}

async function streamAgenticRun(
  targetServiceUrl: string,
  authToken: string,
  payload: Record<string, unknown>,
  onStatus: (message: string) => void,
): Promise<void> {
  const response = await fetch(`${targetServiceUrl}/run`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${authToken}`,
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || "Failed to start coder agent run");
  }

  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error("Coder service returned no stream");
  }

  const decoder = new TextDecoder();
  let buffer = "";
  let currentEvent = "";
  let receivedTerminalEvent = false;

  const processLine = (line: string) => {
    if (line.startsWith("event: ")) {
      currentEvent = line.slice(7).trim();
      return;
    }

    if (!line.startsWith("data: ") || !currentEvent) return;

    let data: Record<string, unknown>;
    try {
      data = JSON.parse(line.slice(6));
    } catch {
      currentEvent = "";
      return;
    }

    if (currentEvent === "status" && typeof data.message === "string") {
      onStatus(data.message);
    }

    if (currentEvent === "error") {
      receivedTerminalEvent = true;
      throw new Error(
        typeof data.message === "string" ? data.message : "Coder agent failed",
      );
    }

    if (currentEvent === "complete") {
      receivedTerminalEvent = true;
      onStatus("Coder agent completed.");
      return;
    }

    currentEvent = "";
  };

  while (true) {
    const { done, value } = await reader.read();
    if (done) {
      const remainingLines = buffer ? buffer.split("\n") : [];
      buffer = "";
      for (const line of remainingLines) {
        processLine(line);
      }
      break;
    }

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split("\n");
    buffer = lines.pop() || "";

    for (const line of lines) {
      processLine(line);
      if (receivedTerminalEvent) {
        return;
      }
    }
  }

  if (receivedTerminalEvent) {
    return;
  }

  throw new Error("Coder agent stream ended before reporting completion.");
}

export function AdminFamiliesPage() {
  const { user, session, loading: authLoading } = useAuth();

  const [projects, setProjects] = useState<FamilyProjectSummary[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [projectsError, setProjectsError] = useState<string | null>(null);

  const [selectedFamilyProjectId, setSelectedFamilyProjectId] = useState<string>("");
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [configError, setConfigError] = useState<string | null>(null);
  const [familyConfig, setFamilyConfig] = useState<FamilyConfigResponse | null>(null);

  const [selectedCombos, setSelectedCombos] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState("");
  const [unpublishUnselected, setUnpublishUnselected] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState<string | null>(null);
  const [generateSummary, setGenerateSummary] = useState<GenerateForksResponse["summary"] | null>(null);
  const [syncFailures, setSyncFailures] = useState<Array<{ projectId: string; error: string }>>([]);
  const [generateProgress, setGenerateProgress] = useState<{ current: number; total: number } | null>(null);
  const [generateCurrentLabel, setGenerateCurrentLabel] = useState("");

  const [newFamilyName, setNewFamilyName] = useState("");
  const [newFamilyPrompt, setNewFamilyPrompt] = useState("");
  const [coderModel, setCoderModel] = useState<CoderModelId>("claude-opus-4-6");
  const [coderModelOpen, setCoderModelOpen] = useState(false);
  const [creatingFamily, setCreatingFamily] = useState(false);
  const [createFamilyError, setCreateFamilyError] = useState<string | null>(null);
  const [createFamilyStatus, setCreateFamilyStatus] = useState<string[]>([]);
  const [showOverwriteModal, setShowOverwriteModal] = useState(false);
  const existingProjectIdRef = useRef<string | null>(null);

  const [familyLogoEmoji, setFamilyLogoEmoji] = useState("");
  const [generatingLogoEmoji, setGeneratingLogoEmoji] = useState(false);
  const [logoEmojiChoices, setLogoEmojiChoices] = useState<string[]>([]);
  const [seenGeneratedLogoEmojis, setSeenGeneratedLogoEmojis] = useState<string[]>([]);
  const [savingLogoEmoji, setSavingLogoEmoji] = useState(false);
  const [assigningRandomEmojis, setAssigningRandomEmojis] = useState(false);
  const [randomEmojiResult, setRandomEmojiResult] = useState<{ assigned: number; skipped: number } | null>(null);
  const [randomEmojiError, setRandomEmojiError] = useState<string | null>(null);

  const appendCreateStatus = useCallback((line: string) => {
    setCreateFamilyStatus((prev) => [...prev, line]);
  }, []);

  useEffect(() => {
    document.title = "Admin Families - Llambada";
    return () => {
      document.title = "Llambada";
    };
  }, []);

  const loadProjects = useCallback(async () => {
    if (!session || !user) return;
    setLoadingProjects(true);
    setProjectsError(null);

    const { data, error } = await dbSelect<FamilyProjectSummary[]>("projects", {
      token: session.access_token,
      select: "id,name,is_published,updated_at,family_creation_prompt,logo_emoji",
      filters: {
        user_id: `eq.${user.id}`,
        forked_from: "is.null",
        is_family_template: "eq.true",
      },
      order: "updated_at.desc",
    });

    if (error) {
      setProjectsError(error.message);
      setProjects([]);
    } else {
      const next = data || [];
      setProjects(next);
      if (!selectedFamilyProjectId && next.length > 0) {
        setSelectedFamilyProjectId(next[0].id);
      }
    }

    setLoadingProjects(false);
  }, [session, user, selectedFamilyProjectId]);

  const loadFamilyConfig = useCallback(async (familyProjectId: string) => {
    if (!session || !familyProjectId) return;
    setLoadingConfig(true);
    setConfigError(null);
    setGenerateError(null);
    setGenerateSummary(null);
    setSyncFailures([]);

    const { data, error } = await invokeFunction<FamilyConfigResponse>(
      "manage-family-forks",
      {
        action: "get_family_config",
        familyProjectId,
      },
      { token: session.access_token },
    );

    if (error || !data) {
      setConfigError(error?.message || "Failed to load family config");
      setFamilyConfig(null);
      setSelectedCombos(new Set());
    } else {
      setFamilyConfig(data);
      // Pre-select combinations that already have existing forks
      const existingKeys = new Set(
        (data.existingForks || [])
          .filter((fork) => fork.family_params)
          .map((fork) => combinationKey(fork.family_params!))
      );
      setSelectedCombos(existingKeys);
    }

    setLoadingConfig(false);
  }, [session]);

  useEffect(() => {
    if (user?.email === ADMIN_EMAIL) {
      loadProjects();
    }
  }, [user, loadProjects]);

  useEffect(() => {
    if (selectedFamilyProjectId) {
      loadFamilyConfig(selectedFamilyProjectId);
    }
  }, [selectedFamilyProjectId, loadFamilyConfig]);

  // Sync emoji state when selected family project changes
  useEffect(() => {
    const proj = projects.find((p) => p.id === selectedFamilyProjectId);
    setFamilyLogoEmoji(proj?.logo_emoji || "");
    setLogoEmojiChoices([]);
    setSeenGeneratedLogoEmojis([]);
  }, [selectedFamilyProjectId, projects]);

  const selectedProject = useMemo(
    () => projects.find((p) => p.id === selectedFamilyProjectId) || null,
    [projects, selectedFamilyProjectId],
  );

  const generateLogoEmoji = async () => {
    if (!session || !selectedProject) return;
    setGeneratingLogoEmoji(true);

    try {
      const appName = selectedProject.name || "Mini-app";

      const { data: publishedLogos, error: publishedLogoError } = await dbSelect<Array<{ logo_emoji: string | null }>>(
        "projects",
        {
          token: session.access_token,
          select: "logo_emoji",
          filters: {
            is_published: "eq.true",
            logo_emoji: "not.is.null",
          },
        },
      );

      if (publishedLogoError) throw publishedLogoError;

      const usedEmojis = new Set(
        (publishedLogos || [])
          .map((item) => normalizeLogoEmoji(item.logo_emoji || ""))
          .filter((emoji) => isLogoEmojiSet(emoji)),
      );

      const seenSet = new Set(seenGeneratedLogoEmojis);
      const nextSeenSet = new Set(seenSet);

      const { data, error } = await invokeFunction<{ emojis?: string[] }>(
        "generate-description",
        {
          action: "emoji_search",
          query: appName,
          topK: 30,
        },
        { token: session.access_token },
      );

      if (error) throw error;

      if (!error && Array.isArray(data?.emojis)) {
        const normalizedResults = data.emojis
          .map((emoji) => normalizeLogoEmoji(emoji))
          .filter((emoji) => isLogoEmojiSet(emoji));

        const available = normalizedResults.filter(
          (emoji) => !usedEmojis.has(emoji) && !nextSeenSet.has(emoji),
        );

        if (available.length > 0) {
          const choices = Array.from(new Set(available)).slice(0, 6);
          choices.forEach((emoji) => nextSeenSet.add(emoji));
          setLogoEmojiChoices(choices);
          setSeenGeneratedLogoEmojis(Array.from(nextSeenSet));
          return;
        }
      }

      setFamilyLogoEmoji(FALLBACK_LOGO_EMOJI);
      setLogoEmojiChoices([FALLBACK_LOGO_EMOJI]);
      setSeenGeneratedLogoEmojis(mergeUniqueStrings(seenGeneratedLogoEmojis, [FALLBACK_LOGO_EMOJI]));
    } catch (err) {
      console.error("Failed to generate logo emoji:", err);
      setFamilyLogoEmoji(FALLBACK_LOGO_EMOJI);
      setLogoEmojiChoices([FALLBACK_LOGO_EMOJI]);
      setSeenGeneratedLogoEmojis(mergeUniqueStrings(seenGeneratedLogoEmojis, [FALLBACK_LOGO_EMOJI]));
    } finally {
      setGeneratingLogoEmoji(false);
    }
  };

  const saveLogoEmoji = async (emoji: string) => {
    if (!session || !selectedFamilyProjectId) return;
    const normalized = normalizeLogoEmoji(emoji);
    setFamilyLogoEmoji(normalized);
    setLogoEmojiChoices([]);
    setSavingLogoEmoji(true);

    const { error } = await dbUpdate(
      "projects",
      { logo_emoji: normalized || null },
      { id: `eq.${selectedFamilyProjectId}` },
      { token: session.access_token, returning: false },
    );

    if (error) {
      console.error("Failed to save logo emoji:", error);
    } else {
      // Update local projects list so it stays in sync
      setProjects((prev) =>
        prev.map((p) => (p.id === selectedFamilyProjectId ? { ...p, logo_emoji: normalized || null } : p)),
      );
    }
    setSavingLogoEmoji(false);
  };

  const assignRandomEmojisToForks = async () => {
    if (!session || !familyConfig) return;
    const forks = familyConfig.existingForks || [];
    if (forks.length === 0) return;

    setAssigningRandomEmojis(true);
    setRandomEmojiResult(null);
    setRandomEmojiError(null);

    try {
      // 1. Fetch all emojis currently used by ANY published project on the platform
      const { data: publishedLogos, error: pubErr } = await dbSelect<Array<{ logo_emoji: string | null }>>(
        "projects",
        {
          token: session.access_token,
          select: "logo_emoji",
          filters: {
            is_published: "eq.true",
            logo_emoji: "not.is.null",
          },
        },
      );

      if (pubErr) throw pubErr;

      const usedEmojis = new Set(
        (publishedLogos || [])
          .map((item) => normalizeLogoEmoji(item.logo_emoji || ""))
          .filter((e) => isLogoEmojiSet(e)),
      );

      // 2. Build available pool (excluding already-used emojis)
      const available = EMOJI_POOL.filter((e) => {
        const n = normalizeLogoEmoji(e);
        return isLogoEmojiSet(n) && !usedEmojis.has(n);
      });

      if (available.length < forks.length) {
        setRandomEmojiError(
          `Not enough unused emojis. Need ${forks.length} but only ${available.length} available.`,
        );
        setAssigningRandomEmojis(false);
        return;
      }

      // 3. Shuffle (Fisher-Yates) and pick forks.length emojis
      const shuffled = [...available];
      for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }
      const picked = shuffled.slice(0, forks.length);

      // 4. Update each fork's logo_emoji
      let assigned = 0;
      let skipped = 0;

      for (let i = 0; i < forks.length; i++) {
        const fork = forks[i];
        const emoji = normalizeLogoEmoji(picked[i]);

        const { error: updateErr } = await dbUpdate(
          "projects",
          { logo_emoji: emoji },
          { id: `eq.${fork.id}` },
          { token: session.access_token, returning: false },
        );

        if (updateErr) {
          console.error(`Failed to update emoji for fork ${fork.id}:`, updateErr);
          skipped++;
        } else {
          assigned++;
        }
      }

      setRandomEmojiResult({ assigned, skipped });

      // Reload family config to refresh fork list
      if (selectedFamilyProjectId) {
        await loadFamilyConfig(selectedFamilyProjectId);
      }
    } catch (err) {
      setRandomEmojiError(err instanceof Error ? err.message : "Failed to assign emojis");
    } finally {
      setAssigningRandomEmojis(false);
    }
  };

  const paramLabelMaps = useMemo(() => {
    const maps = new Map<string, Map<string, string>>();
    for (const param of familyConfig?.config.parameters || []) {
      const m = new Map<string, string>();
      for (const v of param.values) {
        m.set(v.value, v.label);
      }
      maps.set(param.name, m);
    }
    return maps;
  }, [familyConfig]);

  const filteredCombinations = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const combos = familyConfig?.config.combinations || [];
    if (!term) return combos;
    return combos.filter((combo) => {
      for (const [paramName, paramValue] of Object.entries(combo)) {
        const labelMap = paramLabelMaps.get(paramName);
        const label = (labelMap?.get(paramValue) || paramValue).toLowerCase();
        if (label.includes(term) || paramValue.includes(term)) return true;
      }
      return false;
    });
  }, [familyConfig, searchTerm, paramLabelMaps]);

  const existingForksByCombo = useMemo(() => {
    const map = new Map<string, ExistingFamilyFork>();
    for (const fork of familyConfig?.existingForks || []) {
      if (!fork.family_params) continue;
      map.set(combinationKey(fork.family_params), fork);
    }
    return map;
  }, [familyConfig]);

  const staleForks = useMemo(
    () => (familyConfig?.existingForks || []).filter((fork) => fork.code_updated_after_recreate),
    [familyConfig],
  );

  const toggleCombo = (combo: FamilyCombination) => {
    const key = combinationKey(combo);
    setSelectedCombos((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const selectAllVisible = () => {
    setSelectedCombos((prev) => {
      const next = new Set(prev);
      for (const combo of filteredCombinations) {
        next.add(combinationKey(combo));
      }
      return next;
    });
  };

  const clearAllSelections = () => {
    setSelectedCombos(new Set());
  };

  const handleGenerate = async () => {
    if (!session || !selectedFamilyProjectId || !familyConfig) return;
    const combos = familyConfig.config.combinations
      .filter((combo) => selectedCombos.has(combinationKey(combo)));

    if (combos.length === 0) {
      setGenerateError("Select at least one combination before generating forks.");
      return;
    }

    setGenerating(true);
    setGenerateError(null);
    setGenerateSummary(null);
    setSyncFailures([]);
    setGenerateProgress({ current: 0, total: combos.length });
    setGenerateCurrentLabel("");

    try {
      // 1. Fetch the family project's index.html once
      const { data: htmlData, error: htmlError } = await invokeFunction<{ html: string }>(
        "manage-family-forks",
        { action: "get_family_html", familyProjectId: selectedFamilyProjectId },
        { token: session.access_token },
      );

      if (htmlError || !htmlData?.html) {
        setGenerateError(htmlError?.message || "Failed to load family index.html");
        setGenerating(false);
        setGenerateProgress(null);
        return;
      }

      const truncatedHtml = htmlData.html.length > 30000
        ? htmlData.html.substring(0, 30000) + "\n...(truncated)"
        : htmlData.html;

      let createdCount = 0;
      let updatedCount = 0;
      let skippedCount = 0;
      const failures: Array<{ projectId: string; error: string }> = [];

      // 2. Process each combination one by one
      for (let i = 0; i < combos.length; i++) {
        const combo = combos[i];
        const forkTitle = interpolateTemplate(
          familyConfig.config.nameTemplate,
          combo,
          familyConfig.config.parameters,
        );

        setGenerateProgress({ current: i + 1, total: combos.length });
        setGenerateCurrentLabel(forkTitle);

        // 2a. Call Claude Haiku 4.5 to generate a description
        const descriptionPrompt =
          `Return JSON only with keys "description" and "type".\n\n` +
          `Rules:\n` +
          `- "description" must be exactly three sentences for a potential user.\n` +
          `- "type" must be either "game" or "utility".\n` +
          `- Output only valid JSON, no markdown or extra text.\n\n` +
          `The mini-app is called "${forkTitle}".\n\n` +
          truncatedHtml;

        let description = "";
        const { data: aiData, error: aiError } = await invokeFunction<{ text: string }>(
          "anthropic-generate",
          {
            prompt: descriptionPrompt,
            model: "claude-haiku-4-5-20251001",
            maxTokens: 300,
          },
          { token: session.access_token },
        );

        if (aiError || !aiData?.text) {
          // Fallback: use template-based description
          description = interpolateTemplate(
            familyConfig.config.descriptionTemplate,
            combo,
            familyConfig.config.parameters,
          );
        } else {
          try {
            // Strip markdown code fence if Claude wrapped the JSON in one
            let rawText = aiData.text.trim();
            if (rawText.startsWith("```")) {
              rawText = rawText.replace(/^```(?:json)?\s*/i, "").replace(/\s*```$/, "").trim();
            }
            const parsed = JSON.parse(rawText);
            description = parsed.description || "";
          } catch {
            // If JSON parsing fails, use the raw text or fall back to template
            description = aiData.text.trim();
          }
          if (!description) {
            description = interpolateTemplate(
              familyConfig.config.descriptionTemplate,
              combo,
              familyConfig.config.parameters,
            );
          }
        }

        // 2b. Upsert the single fork with the generated description
        const { data: forkData, error: forkError } = await invokeFunction<{ fork: { id: string }; skipped?: boolean }>(
          "manage-family-forks",
          {
            action: "upsert_single_fork",
            familyProjectId: selectedFamilyProjectId,
            combination: combo,
            description,
            publishFork: true,
          },
          { token: session.access_token },
        );

        if (forkError) {
          failures.push({ projectId: forkData?.fork?.id || "unknown", error: forkError.message });
        } else if (forkData?.skipped) {
          skippedCount++;
        } else {
          const key = combinationKey(combo);
          if (existingForksByCombo.has(key)) {
            updatedCount++;
          } else {
            createdCount++;
          }
        }
      }

      // 3. Unpublish unselected forks if requested
      let unpublishedCount = 0;
      if (unpublishUnselected) {
        const { data: unpubData } = await invokeFunction<{ unpublishedCount: number }>(
          "manage-family-forks",
          {
            action: "unpublish_unselected_forks",
            familyProjectId: selectedFamilyProjectId,
            selectedCombinations: combos,
          },
          { token: session.access_token },
        );
        unpublishedCount = unpubData?.unpublishedCount || 0;
      }

      setGenerateSummary({
        selectedCombinations: combos.length,
        createdForks: createdCount,
        updatedForks: updatedCount,
        skippedCustomizedForks: skippedCount,
        unpublishedForks: unpublishedCount,
        syncFailures: failures.length,
      });
      setSyncFailures(failures);
      await loadFamilyConfig(selectedFamilyProjectId);
    } catch (err) {
      setGenerateError(err instanceof Error ? err.message : "Failed to generate forks");
    } finally {
      setGenerating(false);
      setGenerateProgress(null);
      setGenerateCurrentLabel("");
    }
  };

  const doCreateFamily = async () => {
    if (!session || !user) return;

    const name = newFamilyName.trim();
    const prompt = newFamilyPrompt.trim();

    setCreatingFamily(true);
    setCreateFamilyError(null);
    setCreateFamilyStatus([]);

    try {
      // If overwriting, delete the existing project first
      if (existingProjectIdRef.current) {
        appendCreateStatus("Deleting existing project...");
        const { error: deleteError } = await dbDelete(
          "projects",
          { id: `eq.${existingProjectIdRef.current}` },
          { token: session.access_token },
        );
        if (deleteError) {
          throw new Error(`Failed to delete existing project: ${deleteError.message}`);
        }
        existingProjectIdRef.current = null;
      }

      appendCreateStatus("Creating hidden family project...");
      const { data: project, error: projectError } = await dbInsert<{ id: string; name: string }>(
        "projects",
        {
          user_id: user.id,
          name,
          description: `Hidden family template for ${name}`,
          is_published: false,
          is_family_template: true,
          family_creation_prompt: prompt,
          type: "utility",
        },
        { token: session.access_token },
      );

      if (projectError || !project) {
        throw new Error(projectError?.message || "Failed to create family project");
      }

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

      appendCreateStatus("Initializing project files...");
      const initResponse = await fetch(
        `${supabaseUrl}/functions/v1/project-code?projectId=${project.id}`,
        {
          method: "POST",
          headers: {
            "Authorization": `Bearer ${session.access_token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ action: "init" }),
        },
      );

      if (!initResponse.ok) {
        const text = await initResponse.text();
        throw new Error(text || "Failed to initialize project code");
      }

      appendCreateStatus("Provisioning cloud run infrastructure...");
      const { error: infraError } = await invokeFunction<{ serviceName: string; serviceUrl: string }>(
        "create-project-infrastructure",
        { projectId: project.id, projectName: name },
        { token: session.access_token },
      );

      if (infraError) {
        appendCreateStatus(`Infrastructure warning: ${infraError.message}`);
      }

      appendCreateStatus("Creating setup conversation...");
      const { data: conversation, error: conversationError } = await dbInsert<{ id: string }>(
        "conversations",
        {
          project_id: project.id,
          title: "Family setup",
        },
        { token: session.access_token },
      );

      if (conversationError || !conversation) {
        throw new Error(conversationError?.message || "Failed to create setup conversation");
      }

      const { data: userMessage, error: userMessageError } = await dbInsert<{ id: string }>(
        "messages",
        {
          conversation_id: conversation.id,
          role: "user",
          content: prompt,
        },
        { token: session.access_token },
      );

      if (userMessageError || !userMessage) {
        throw new Error(userMessageError?.message || "Failed to create initial user message");
      }

      appendCreateStatus("Authenticating coder agent...");
      const authResponse = await fetch(`${supabaseUrl}/functions/v1/agentic-coder-auth`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          conversationId: conversation.id,
          message: prompt,
          currentUserMessageId: userMessage.id,
        }),
      });

      if (!authResponse.ok) {
        const text = await authResponse.text();
        throw new Error(text || "Failed to authenticate coder agent");
      }

      const authData = await authResponse.json() as AgenticAuthResponse;

      appendCreateStatus("Running coder agent to scaffold family app...");
      await streamAgenticRun(
        authData.serviceUrl,
        authData.token,
        {
          message: prompt,
          conversationId: authData.conversationId,
          currentUserMessageId: userMessage.id,
          projectId: authData.projectId,
          isFirstMessage: authData.isFirstMessage,
          isForkedProject: authData.isForkedProject,
          codeZipBase64: authData.codeZipBase64,
          codeZipGcsPath: authData.codeZipGcsPath,
          model: coderModel,
        },
        (line) => appendCreateStatus(line),
      );

      appendCreateStatus("Family app creation finished.");
      setNewFamilyName("");
      setNewFamilyPrompt("");

      await loadProjects();
      setSelectedFamilyProjectId(project.id);
      await loadFamilyConfig(project.id);
    } catch (error) {
      setCreateFamilyError(error instanceof Error ? error.message : "Failed to create family app");
    } finally {
      setCreatingFamily(false);
    }
  };

  const handleCreateFamily = async (e: FormEvent) => {
    e.preventDefault();
    if (!session || !user) return;

    const name = newFamilyName.trim();
    const prompt = newFamilyPrompt.trim();

    if (!name || !prompt) {
      setCreateFamilyError("Name and prompt are required.");
      return;
    }

    // Check if a project with this name already exists
    const { data: existing } = await dbSelect<Array<{ id: string; name: string }>>("projects", {
      token: session.access_token,
      select: "id,name",
      filters: {
        user_id: `eq.${user.id}`,
        name: `eq.${name}`,
        is_family_template: "eq.true",
        forked_from: "is.null",
      },
      limit: 1,
    });

    if (existing && existing.length > 0) {
      existingProjectIdRef.current = existing[0].id;
      setShowOverwriteModal(true);
      return;
    }

    existingProjectIdRef.current = null;
    doCreateFamily();
  };

  if (authLoading) return <PageLoader />;
  if (!user || user.email !== ADMIN_EMAIL) {
    return <Navigate to="/dashboard" replace />;
  }

  const parameters = familyConfig?.config.parameters || [];

  return (
    <Layout>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        <div className="flex items-center gap-3">
          <Link
            to="/admin"
            className="flex h-10 w-10 items-center justify-center rounded-lg bg-theme-surface hover:bg-theme-bg-muted transition-colors"
            title="Back to Admin"
          >
            <ArrowLeft className="h-5 w-5 text-theme-text-muted" />
          </Link>
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-500/10 text-blue-500">
            <Layers className="h-6 w-6" />
          </div>
          <div>
            <h1 className="font-display text-2xl font-bold text-theme-text">
              Family Apps Admin
            </h1>
            <p className="text-theme-text-secondary text-sm">
              Create hidden family apps with name + prompt, then create/recreate fork mini-apps from selected parameter combinations.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-[1.2fr_1fr] gap-6">
          <form onSubmit={handleCreateFamily} className="card p-4 sm:p-6 space-y-4">
            <div className="flex items-center gap-2">
              <PlusCircle className="h-5 w-5 text-accent-500" />
              <h2 className="text-lg font-semibold text-theme-text">Create Family App</h2>
            </div>

            <label className="block">
              <span className="text-sm font-medium text-theme-text-secondary">Family app name</span>
              <input
                className="input mt-2"
                placeholder="Pandoc converters"
                value={newFamilyName}
                onChange={(e) => setNewFamilyName(e.target.value)}
                disabled={creatingFamily}
                required
              />
            </label>

            <label className="block">
              <span className="text-sm font-medium text-theme-text-secondary">Coder prompt</span>
              <textarea
                className="input mt-2 min-h-[160px]"
                placeholder="Describe the CLI tool, parameters, and what combinations should be supported in family.json"
                value={newFamilyPrompt}
                onChange={(e) => setNewFamilyPrompt(e.target.value)}
                disabled={creatingFamily}
                required
              />
            </label>

            <div className="relative">
              <label className="text-sm font-medium text-theme-text-secondary block mb-2">Agentic coder</label>
              <button
                type="button"
                onClick={() => setCoderModelOpen(!coderModelOpen)}
                disabled={creatingFamily}
                className="flex items-center justify-between w-full px-3 py-2 rounded-lg bg-theme-surface border border-theme-border-muted hover:bg-theme-surface-hover transition-colors disabled:opacity-50"
              >
                <div className="text-left">
                  <div className="text-sm font-medium text-theme-text">{CODER_MODELS.find((m) => m.id === coderModel)?.name}</div>
                  <div className="text-xs text-theme-text-muted">{CODER_MODELS.find((m) => m.id === coderModel)?.description}</div>
                </div>
                <ChevronDown className={`h-4 w-4 text-theme-text-muted transition-transform ${coderModelOpen ? 'rotate-180' : ''}`} />
              </button>
              {coderModelOpen && (
                <div className="absolute top-full left-0 mt-1 w-full rounded-lg bg-theme-surface border border-theme-border-muted shadow-lg overflow-hidden z-10">
                  {CODER_MODELS.map((model) => (
                    <button
                      key={model.id}
                      type="button"
                      onClick={() => { setCoderModel(model.id); setCoderModelOpen(false); }}
                      className={`w-full flex items-center justify-between px-3 py-2.5 text-left hover:bg-theme-surface-hover transition-colors ${
                        coderModel === model.id ? 'bg-theme-surface-hover' : ''
                      }`}
                    >
                      <div>
                        <div className="text-sm font-medium text-theme-text">{model.name}</div>
                        <div className="text-xs text-theme-text-muted">{model.description}</div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <details className="rounded-lg border border-theme-border-muted bg-theme-surface/30 px-3 py-2">
              <summary className="cursor-pointer text-sm font-medium text-theme-text-secondary">
                Example prompt for family app
              </summary>
              <pre className="mt-2 whitespace-pre-wrap text-xs text-theme-text-secondary">
                {FAMILY_PROMPT_EXAMPLE}
              </pre>
            </details>

            {createFamilyError && (
              <div className="rounded-lg border border-theme-error-border bg-theme-error-bg px-4 py-3 text-sm text-theme-error">
                {createFamilyError}
              </div>
            )}

            {createFamilyStatus.length > 0 && (
              <div className="rounded-lg border border-theme-border-muted bg-theme-surface/40 px-3 py-2 text-xs text-theme-text-secondary max-h-40 overflow-auto space-y-1">
                {createFamilyStatus.map((line, index) => (
                  <div key={`${line}-${index}`}>{line}</div>
                ))}
              </div>
            )}

            <button type="submit" className="btn btn-primary" disabled={creatingFamily}>
              {creatingFamily ? <LoadingSpinner size="sm" /> : "Create Family App"}
            </button>
          </form>

          <div className="card p-4 sm:p-6 space-y-4">
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-lg font-semibold text-theme-text">Family Apps</h2>
              <button onClick={loadProjects} className="btn btn-secondary" disabled={loadingProjects}>
                {loadingProjects ? <LoadingSpinner size="sm" /> : <RefreshCw className="h-4 w-4" />}
                Refresh
              </button>
            </div>

            {projectsError && (
              <div className="rounded-lg border border-theme-error-border bg-theme-error-bg px-4 py-3 text-sm text-theme-error">
                {projectsError}
              </div>
            )}

            <div className="space-y-2 max-h-[360px] overflow-auto">
              {projects.map((project) => (
                <button
                  key={project.id}
                  onClick={() => setSelectedFamilyProjectId(project.id)}
                  className={`w-full text-left rounded-lg border px-3 py-2 transition-colors ${
                    selectedFamilyProjectId === project.id
                      ? "border-accent-500 bg-accent-500/10"
                      : "border-theme-border-muted hover:bg-theme-surface/40"
                  }`}
                >
                  <div className="text-sm font-semibold text-theme-text truncate">{project.name}</div>
                  <div className="text-xs text-theme-text-muted">Updated {new Date(project.updated_at).toLocaleString()}</div>
                </button>
              ))}
              {projects.length === 0 && !loadingProjects && (
                <p className="text-sm text-theme-text-secondary">No family apps yet.</p>
              )}
            </div>
          </div>
        </div>

        {selectedFamilyProjectId && (
          <div className="card p-4 sm:p-6 space-y-4">
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-lg font-semibold text-theme-text">Selected Family</h2>
              <div className="flex items-center gap-2">
                <Link
                  to={`/project/${selectedFamilyProjectId}`}
                  className="btn btn-secondary inline-flex items-center gap-1.5"
                  target="_blank"
                >
                  Open project <ExternalLink className="h-3.5 w-3.5" />
                </Link>
                <button
                  onClick={() => loadFamilyConfig(selectedFamilyProjectId)}
                  className="btn btn-secondary"
                  disabled={loadingConfig}
                >
                  {loadingConfig ? <LoadingSpinner size="sm" /> : "Load family.json"}
                </button>
              </div>
            </div>

            {configError && (
              <div className="rounded-lg border border-theme-error-border bg-theme-error-bg px-4 py-3 text-sm text-theme-error">
                {configError}
              </div>
            )}

            {familyConfig && (
              <div className="rounded-lg border border-theme-border-muted bg-theme-surface/40 px-4 py-3 text-sm text-theme-text-secondary">
                Loaded from <code>{familyConfig.config.familyJsonPath}</code> with config ref <code>{familyConfig.config.configRef}</code>.
                {" "}Parameters: {parameters.map((p) => p.label).join(", ")}.
                {" "}{familyConfig.config.combinations.length} combinations available.
              </div>
            )}

            {/* Logo emoji selector */}
            <div>
              <label className="block text-sm font-medium text-theme-text-secondary mb-2">Logo emoji</label>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-theme-text-muted">
                    {familyLogoEmoji ? "Current emoji" : "No emoji selected"}
                    {savingLogoEmoji && " — saving…"}
                  </span>
                  <button
                    type="button"
                    onClick={generateLogoEmoji}
                    disabled={generatingLogoEmoji}
                    className="flex items-center gap-1.5 text-xs text-blue-600 dark:text-blue-400 hover:text-blue-500 dark:hover:text-blue-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {generatingLogoEmoji ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                    {generatingLogoEmoji ? "Generating..." : "Generate"}
                  </button>
                </div>

                <div className="p-4 rounded-lg bg-theme-surface/50 border border-theme-border-muted min-h-[100px] flex flex-col items-center justify-center gap-3 transition-all">
                  {logoEmojiChoices.length > 0 ? (
                    <div className="w-full animate-fade-in">
                      <p className="text-xs text-center text-theme-text-muted mb-3">Choose one to apply:</p>
                      <div className="flex flex-wrap justify-center gap-3">
                        {logoEmojiChoices.map((emoji) => (
                          <button
                            key={emoji}
                            type="button"
                            onClick={() => saveLogoEmoji(emoji)}
                            className="h-12 w-12 text-2xl flex items-center justify-center rounded-xl border border-theme-border-muted bg-theme-surface hover:border-theme-primary hover:bg-theme-primary/5 hover:scale-110 transition-all duration-200"
                            title={"Select " + emoji}
                          >
                            {emoji}
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : familyLogoEmoji ? (
                    <div className="text-center animate-fade-in">
                      <div className="h-20 w-20 mx-auto text-5xl flex items-center justify-center rounded-2xl bg-theme-surface border border-theme-border shadow-sm mb-2">
                        {familyLogoEmoji}
                      </div>
                      <p className="text-xs text-theme-success font-medium flex items-center justify-center gap-1">
                        <Check className="h-3 w-3" /> Selected
                      </p>
                    </div>
                  ) : (
                    <div className="text-center py-2 text-theme-text-muted animate-fade-in">
                      <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-theme-surface border border-theme-border-dashed mb-2 opacity-50">
                        <Sparkles className="h-5 w-5" />
                      </div>
                      <p className="text-sm">No emoji set</p>
                      <button
                        type="button"
                        onClick={generateLogoEmoji}
                        className="text-xs text-accent-500 hover:text-accent-600 hover:underline mt-1"
                      >
                        Generate options
                      </button>
                    </div>
                  )}
                </div>
              </div>
              <p className="mt-1.5 text-xs text-theme-text-muted">Required for publishing. Applied to all forks.</p>
            </div>
          </div>
        )}

        {familyConfig && (
          <div className="grid grid-cols-1 xl:grid-cols-[1.3fr_1fr] gap-6">
            <div className="card p-4 sm:p-6 space-y-4">
              <div className="flex items-center justify-between gap-3">
                <h2 className="text-lg font-semibold text-theme-text">Combination Selection</h2>
                <div className="text-sm text-theme-text-secondary">
                  Selected: <span className="font-semibold text-theme-text">{selectedCombos.size}</span>
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                <button onClick={selectAllVisible} className="btn btn-secondary">Select visible</button>
                <button onClick={clearAllSelections} className="btn btn-secondary">Clear all</button>
              </div>

              <input
                className="input"
                placeholder="Filter combinations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />

              {familyConfig.config.combinations.length > 100 && (
                <div className="rounded-lg border border-yellow-500/30 bg-yellow-500/10 px-4 py-2 text-xs text-yellow-300">
                  {familyConfig.config.combinations.length} combinations available. Consider narrowing the selection.
                </div>
              )}

              <div className="max-h-[480px] overflow-auto rounded-lg border border-theme-border-muted">
                <table className="w-full text-sm">
                  <thead className="bg-theme-surface border-b border-theme-border-muted sticky top-0 z-10">
                    <tr>
                      <th className="px-3 py-2 text-left font-semibold text-theme-text-muted">Use</th>
                      {parameters.map((p) => (
                        <th key={p.name} className="px-3 py-2 text-left font-semibold text-theme-text-muted">{p.label}</th>
                      ))}
                      <th className="px-3 py-2 text-left font-semibold text-theme-text-muted">Existing fork</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCombinations.map((combo) => {
                      const key = combinationKey(combo);
                      const checked = selectedCombos.has(key);
                      const existing = existingForksByCombo.get(key);
                      return (
                        <tr key={key} className="border-b border-theme-border-muted/50 last:border-b-0">
                          <td className="px-3 py-2">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleCombo(combo)}
                            />
                          </td>
                          {parameters.map((p) => (
                            <td key={p.name} className="px-3 py-2 text-theme-text">
                              {paramLabelMaps.get(p.name)?.get(combo[p.name]) || combo[p.name]}
                            </td>
                          ))}
                          <td className="px-3 py-2">
                            {existing?.public_id ? (
                              <Link
                                to={`/p/${existing.public_id}${existing.slug ? `/${existing.slug}` : ""}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-accent-400 hover:text-accent-300"
                              >
                                Open
                                <ExternalLink className="h-3.5 w-3.5" />
                              </Link>
                            ) : (
                              <span className="text-theme-text-muted">No</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <label className="flex items-center gap-2 text-sm text-theme-text-secondary">
                <input
                  type="checkbox"
                  checked={unpublishUnselected}
                  onChange={(e) => setUnpublishUnselected(e.target.checked)}
                />
                Unpublish existing family forks that are not selected
              </label>

              {generateError && (
                <div className="rounded-lg border border-theme-error-border bg-theme-error-bg px-4 py-3 text-sm text-theme-error">
                  {generateError}
                </div>
              )}

              <button onClick={handleGenerate} className="btn btn-primary" disabled={generating || selectedCombos.size === 0}>
                {generating ? <LoadingSpinner size="sm" /> : "Create / Recreate Selected Forks"}
              </button>

              {generateProgress && (
                <div className="rounded-lg border border-theme-border-muted bg-theme-surface/40 px-4 py-3 text-sm">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-theme-text-secondary">
                      Fork {generateProgress.current} / {generateProgress.total}
                    </span>
                    <span className="font-semibold text-theme-text">
                      {Math.round((generateProgress.current / generateProgress.total) * 100)}%
                    </span>
                  </div>
                  {generateCurrentLabel && (
                    <div className="text-xs text-theme-text-muted truncate mb-2">
                      {generateCurrentLabel}
                    </div>
                  )}
                  <div className="h-1.5 rounded-full bg-theme-surface overflow-hidden">
                    <div
                      className="h-full rounded-full bg-accent-500 transition-all duration-300"
                      style={{ width: `${(generateProgress.current / generateProgress.total) * 100}%` }}
                    />
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-6">
              <div className="card p-4 sm:p-6 space-y-3">
                <h2 className="text-lg font-semibold text-theme-text">Generation Result</h2>
                {!generateSummary ? (
                  <p className="text-sm text-theme-text-secondary">Run generation to see summary.</p>
                ) : (
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between"><span>Selected combinations</span><span className="font-semibold">{generateSummary.selectedCombinations}</span></div>
                    <div className="flex justify-between"><span>Created forks</span><span className="font-semibold">{generateSummary.createdForks}</span></div>
                    <div className="flex justify-between"><span>Updated forks</span><span className="font-semibold">{generateSummary.updatedForks}</span></div>
                    <div className="flex justify-between"><span>Skipped customized forks</span><span className="font-semibold">{generateSummary.skippedCustomizedForks}</span></div>
                    <div className="flex justify-between"><span>Unpublished forks</span><span className="font-semibold">{generateSummary.unpublishedForks}</span></div>
                    <div className="flex justify-between"><span>Code sync failures</span><span className="font-semibold">{generateSummary.syncFailures}</span></div>
                  </div>
                )}
                {syncFailures.length > 0 && (
                  <div className="rounded-lg border border-theme-error-border bg-theme-error-bg px-3 py-2 text-xs text-theme-error space-y-1">
                    {syncFailures.map((item) => (
                      <div key={item.projectId}>
                        <strong>{item.projectId}</strong>: {item.error}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="card p-4 sm:p-6 space-y-3">
                <div className="flex items-center justify-between gap-3">
                  <h2 className="text-lg font-semibold text-theme-text">Existing Family Forks</h2>
                  {(familyConfig.existingForks || []).length > 0 && (
                    <button
                      onClick={assignRandomEmojisToForks}
                      className="btn btn-secondary inline-flex items-center gap-1.5"
                      disabled={assigningRandomEmojis}
                    >
                      {assigningRandomEmojis ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Dice5 className="h-3.5 w-3.5" />}
                      {assigningRandomEmojis ? "Assigning..." : "Assign Random Emojis"}
                    </button>
                  )}
                </div>
                {randomEmojiError && (
                  <div className="rounded-lg border border-theme-error-border bg-theme-error-bg px-3 py-2 text-xs text-theme-error">
                    {randomEmojiError}
                  </div>
                )}
                {randomEmojiResult && (
                  <div className="rounded-lg border border-theme-success-border bg-theme-success-bg px-3 py-2 text-xs text-theme-success">
                    Assigned random emojis to {randomEmojiResult.assigned} fork(s).
                    {randomEmojiResult.skipped > 0 && ` ${randomEmojiResult.skipped} failed.`}
                  </div>
                )}
                {staleForks.length > 0 && (
                  <div className="rounded-lg border border-theme-warning-border bg-theme-warning-bg px-3 py-2 text-xs text-theme-warning">
                    {staleForks.length} fork(s) have code changes newer than their last create/recreate time.
                  </div>
                )}
                {staleForks.length === 0 && (familyConfig.existingForks || []).length > 0 && (
                  <div className="rounded-lg border border-theme-success-border bg-theme-success-bg px-3 py-2 text-xs text-theme-success">
                    All forks are up to date with their last create/recreate run.
                  </div>
                )}
                <div className="max-h-[360px] overflow-auto space-y-2">
                  {(familyConfig.existingForks || []).map((fork) => (
                    <div key={fork.id} className="rounded-lg border border-theme-border-muted px-3 py-2">
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <Link
                            to={`/project/${fork.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm font-semibold text-accent-400 hover:text-accent-300 truncate block"
                          >
                            {fork.name}
                          </Link>
                          <div className="text-xs text-theme-text-muted">
                            {fork.family_params ? comboLabel(fork.family_params, parameters) : "?"}
                          </div>
                        </div>
                        {fork.public_id && (
                          <Link
                            to={`/p/${fork.public_id}${fork.slug ? `/${fork.slug}` : ""}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-accent-400 hover:text-accent-300 text-sm shrink-0"
                          >
                            Public
                            <ExternalLink className="h-3.5 w-3.5" />
                          </Link>
                        )}
                      </div>
                      <div className="mt-1 text-xs text-theme-text-muted">
                        Last create/recreate: {formatDateTime(fork.updated_at)}
                      </div>
                      <div className="text-xs text-theme-text-muted">
                        Last code update: {formatDateTime(fork.code_updated_at)}
                      </div>
                      {fork.code_updated_after_recreate && (
                        <div className="mt-1 text-xs text-theme-warning">
                          Needs recreate: code was updated after the last create/recreate.
                        </div>
                      )}
                    </div>
                  ))}
                  {(familyConfig.existingForks || []).length === 0 && (
                    <p className="text-sm text-theme-text-secondary">No family forks yet.</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <Modal isOpen={showOverwriteModal} onClose={() => setShowOverwriteModal(false)} title="Project already exists" size="sm">
        <p className="text-sm text-theme-text-secondary">
          A family app named <strong className="text-theme-text">{newFamilyName.trim()}</strong> already exists. Overwriting will delete the existing project and all its forks.
        </p>
        <div className="flex gap-3 mt-4">
          <button
            className="btn btn-secondary flex-1"
            onClick={() => setShowOverwriteModal(false)}
          >
            Cancel
          </button>
          <button
            className="btn btn-danger flex-1"
            onClick={() => {
              setShowOverwriteModal(false);
              doCreateFamily();
            }}
          >
            Overwrite
          </button>
        </div>
      </Modal>
    </Layout>
  );
}
